import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  Button,
  DialogContent,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import styles from './ClientDataDialogCommon.module.scss';
import BaseDialog from '../DialogWrappers/BaseDialog';
import ConfirmationDialog from '../DialogWrappers/ConfirmationDialog';
import { isValidEmail, normalizeWhitespace } from '../../utils/utils';
import { Product, UserType } from '../../constants';
import { getActiveSubscription } from '../../utils/subscriptionUtils';
import InputField from '../atoms/InputField';
import { getSubscriptionOptions } from '../../utils/dashboardUtils';
import { useSessionData } from '../../providers';

import type { Member } from '../../types';

export interface EditClientDialogProps {
  userType: UserType;
  onClose: () => void;
  onSubmit: (
    newMemberData: Member,
    selectedProduct: Product | '',
  ) => Promise<{
    success: boolean;
    emailAlreadyInUse?: boolean;
    pinAlreadyInUse?: boolean;
  }>;
  isOpen: boolean;
  isLoading?: boolean;
  trainers: { id: string; name: string }[];
  member: Member | null;
  gymMembers: Member[];
}

const initialClientData = {
  firstName: '',
  lastName: '',
  email: '',
  pin: '',
  trainerId: '',
  subscription: '',
};

const EditClientDialog: React.FC<EditClientDialogProps> = ({
  userType,
  member,
  isOpen,
  isLoading = false,
  onSubmit,
  onClose,
  trainers,
}) => {
  const { gym } = useSessionData();
  const enable5DigitPin =
    gym?.configuration?.enabledFeatures?.enable5DigitPin ?? false;
  const requiredPinLength = enable5DigitPin ? 5 : 4;
  const [clientData, setClientData] = useState(initialClientData);

  const [firstNameError, setFirstNameError] = useState<boolean>(false);
  const [lastNameError, setLastNameError] = useState<boolean>(false);
  const [pinError, setPinError] = useState<boolean>(false);
  const [pinErrorMessage, setPinErrorMessage] = useState<string>('');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (isOpen && !isLoading && member) {
      setClientData({
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        email: member.email || '',
        pin: member.pin || '',
        trainerId: member.trainerIds[0] || '',
        subscription:
          getActiveSubscription(member?.subscriptions)?.product || '',
      });
      setFirstNameError(false);
      setLastNameError(false);
      setPinError(false);
      setPinErrorMessage('');
      setEmailError(false);
      setEmailErrorMessage('');
      setLoading(false);
      setErrorMessage(null);
    }
  }, [isOpen, isLoading, member]);

  const hasChanges = () => {
    if (!member) return false;
    const subscription =
      getActiveSubscription(member?.subscriptions)?.product || '';
    return (
      (member.firstName || '') !== clientData.firstName ||
      (member.lastName || '') !== clientData.lastName ||
      (member.email || '') !== clientData.email ||
      (member.pin || '') !== clientData.pin ||
      (member.trainerIds?.[0] || '') !== clientData.trainerId ||
      subscription !== clientData.subscription
    );
  };

  const handleRequestClose = () => {
    if (hasChanges()) {
      setIsCloseConfirmationOpen(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    setIsCloseConfirmationOpen(false);
    onClose();
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    const firstName = normalizeWhitespace(clientData.firstName);
    const lastName = normalizeWhitespace(clientData.lastName);
    const pinTrimmed = clientData.pin.trim();
    const pinWrongLength = pinTrimmed.length !== requiredPinLength;
    const validationErrors = {
      firstName: !firstName,
      lastName: !lastName,
      email: !isValidEmail(clientData.email.trim()),
      pin: pinWrongLength,
    };

    setFirstNameError(validationErrors.firstName);
    setLastNameError(validationErrors.lastName);
    setEmailError(validationErrors.email);
    setEmailErrorMessage(
      validationErrors.email ? 'Please enter a valid email address.' : '',
    );
    setPinError(validationErrors.pin);
    setPinErrorMessage(
      pinWrongLength ? `PIN must be ${requiredPinLength} digits` : '',
    );

    if (Object.values(validationErrors).some(Boolean)) return;

    setLoading(true);

    if (!member) return;
    const newMemberData = _.cloneDeep(member);
    newMemberData.firstName = firstName;
    newMemberData.lastName = lastName;
    newMemberData.email = clientData.email.trim();
    newMemberData.pin = pinTrimmed;
    newMemberData.trainerIds = clientData.trainerId
      ? [clientData.trainerId]
      : [];

    try {
      const result = await onSubmit(
        newMemberData,
        clientData.subscription as Product,
      );

      if (result.success) {
        setClientData(initialClientData);
        onClose();
      } else if (result.emailAlreadyInUse) {
        setEmailError(true);
        setEmailErrorMessage('Email already in use');
      } else if (result.pinAlreadyInUse) {
        setPinError(true);
        setPinErrorMessage('PIN already in use');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscriptionOptions = getSubscriptionOptions();

  const renderSubscriptionValue = (value: string) => {
    const match = subscriptionOptions.find((opt) => opt.value === value);
    return match ? match.label : 'None';
  };

  return (
    <>
      <BaseDialog
        onClose={handleRequestClose}
        isOpen={isOpen}
        title={`Edit ${userType} Information`}
        actions={
          <Button
            className={styles.submit}
            onClick={handleSubmit}
            disabled={loading || isLoading}
          >
            {loading ? (
              <CircularProgress size={24} color="primary" />
            ) : (
              <span className={styles.submitText}>{`Save ${userType}`}</span>
            )}
          </Button>
        }
      >
        <DialogContent className={styles.clientDataDialog}>
          {isLoading && (
            <div className={styles.loadingOverlay}>
              <CircularProgress size={40} color="primary" />
            </div>
          )}
          <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
            <div className={styles.row}>
              <div className={styles.rowColumn}>
                <InputField
                  icon={<PersonOutlineOutlinedIcon color="primary" />}
                  title="First Name"
                  inputName="firstName"
                  autoComplete="firstName"
                  value={clientData.firstName}
                  onChange={(firstName) => {
                    setFirstNameError(false);
                    setClientData((prevClientData) => ({
                      ...prevClientData,
                      firstName,
                    }));
                  }}
                  error={firstNameError}
                  validationMessage="Please enter a first name."
                />
              </div>
              <div className={styles.rowColumn}>
                <InputField
                  icon={<PersonOutlineOutlinedIcon color="primary" />}
                  title="Last Name"
                  inputName="lastName"
                  autoComplete="lastName"
                  value={clientData.lastName}
                  onChange={(lastName) => {
                    setLastNameError(false);
                    setClientData((prevClientData) => ({
                      ...prevClientData,
                      lastName,
                    }));
                  }}
                  error={lastNameError}
                  validationMessage="Please enter a last name."
                />
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.rowColumn}>
                <InputField
                  icon={<MailOutlineRoundedIcon color="primary" />}
                  title="Email"
                  inputName="email"
                  autoComplete="email"
                  value={clientData.email}
                  onChange={(email) => {
                    setEmailError(false);
                    setEmailErrorMessage('');
                    setClientData((prevClientData) => ({
                      ...prevClientData,
                      email,
                    }));
                  }}
                  onBlur={() => {
                    const isInvalid = !isValidEmail(clientData.email);
                    setEmailError(isInvalid);
                    setEmailErrorMessage(
                      isInvalid ? 'Please enter a valid email address.' : '',
                    );
                  }}
                  error={emailError}
                  validationMessage={emailErrorMessage}
                />
              </div>
              <div className={styles.rowColumn}>
                <InputField
                  icon={<SupervisorAccountOutlinedIcon color="primary" />}
                  title="Subscription"
                  inputName="subscription"
                  value={clientData.subscription}
                  options={subscriptionOptions}
                  renderValue={renderSubscriptionValue}
                  onChange={(subscription) => {
                    setClientData((prevClientData) => ({
                      ...prevClientData,
                      subscription,
                    }));
                  }}
                />
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.rowColumn}>
                <InputField
                  icon={<PinOutlinedIcon color="primary" />}
                  title="PIN"
                  inputName="pin"
                  autoComplete="off"
                  value={clientData.pin}
                  onChange={(pin) => {
                    setPinError(false);
                    setPinErrorMessage('');
                    setClientData((prevClientData) => ({
                      ...prevClientData,
                      pin: pin.replace(/\D/g, '').slice(0, requiredPinLength),
                    }));
                  }}
                  error={pinError}
                  validationMessage={pinErrorMessage || `PIN must be ${requiredPinLength} digits.`}
                />
              </div>
              <div className={styles.rowColumn}>
                <InputField
                  icon={<PersonOutlineOutlinedIcon color="primary" />}
                  title="Trainer"
                  inputName="trainerId"
                  value={clientData.trainerId}
                  options={trainers.map(({ id, name }) => ({
                    value: id,
                    label: name,
                  }))}
                  onChange={(trainerId) => {
                    setClientData((prevClientData) => ({
                      ...prevClientData,
                      trainerId,
                    }));
                  }}
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </BaseDialog>
      <ConfirmationDialog
        isOpen={isCloseConfirmationOpen}
        onCancel={() => setIsCloseConfirmationOpen(false)}
        onSubmit={() => confirmClose()}
        title="Discard changes?"
        text={`You have unsaved changes. Do you want to discard them?`}
        submitText="Discard"
        cancelText="Keep Editing"
      />
      {errorMessage && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={() => setErrorMessage(null)}
        >
          <Alert onClose={() => setErrorMessage(null)} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default EditClientDialog;
